import { DateTime } from 'luxon';
import { t } from "i18next";

import { faUser } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonPrimary } from "../atoms/ButtonPrimary";
import Status from "../atoms/Status";
import LabelUI from "./LabelUI";

type BadgesProps = {
  projects: any;
  isLoading: boolean;
  onEdit: (task) => void;
  completeTask: (id: number) => void;
}

const LoadingSkeleton = () => {
  return (
    <ul role="list" className="divide-y divide-gray-100 overflow-x-auto">
      {[1, 2, 3].map((_, index) => (
        <li
          key={index}
          className="flex flex-wrap items-center justify-between gap-x-6 py-5 animate-pulse"
        >
          <div className="min-w-0 ">
            <div className="flex items-start gap-x-3">
              <div className="h-4 bg-gray-300 rounded w-24"></div>
              <div className="h-4 bg-gray-300 rounded w-12"></div>
            </div>
            <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
              <div className="h-3 bg-gray-300 rounded w-16"></div>
              <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                <circle r={1} cx={1} cy={1} />
              </svg>
              <div className="h-3 bg-gray-300 rounded w-24"></div>
            </div>
          </div>
          <div className="w-full lg:w-auto mt-4 lg:mt-0">
            <div className="flex items-start gap-x-3">
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs leading-5 text-gray-500">
              {[1, 2, 3, 4].map((_, index) => (
                <div key={index} className="h-4 bg-gray-300 rounded w-16"></div>
              ))}
            </div>
          </div>
          <div className="flex flex-none items-center gap-x-4 mt-4 lg:mt-0">
            <div className="h-8 bg-gray-300 rounded w-24"></div>
            <div className="h-4 bg-gray-300 rounded w-12"></div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const Badges = ({
  projects,
  isLoading,
  onEdit,
  height = "80",
  completeTask,
}: BadgesProps) => {

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <ul role="list" className={`divide-y divide-gray-100 overflow-x-auto min-h-[200px] h-${height}`}> {/* Ajusta la altura mínima según tus necesidades */}
      {projects && projects?.map((project) => (
        <li
          key={project.id}
          className="flex flex-wrap items-center justify-between gap-x-6 py-5"
        >
          <div className="min-w-0 ">
            {project.assigned ? (
              <div className="mt-1 flex items-center gap-x-2 text-xs leading-3 text-gray-500 justify-start">
                <span className="truncate">
                  <FontAwesomeIcon
                    className="w-4 h-4"
                    icon={faUser}
                  />
                </span>

                <span className="truncate">
                  {project.assigned.name}
                </span>
              </div>
            ) : null}
            <div className="flex items-start gap-x-3">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {project.task ?? project.name}
              </p>
              <Status status={project.state ? 'complete' : 'pending'} />
            </div>
            <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
              <p className="whitespace-nowrap">
                <time dateTime={project.dueDateTime}>
                  {DateTime.fromISO(project.date).toFormat('MMMM d, yyyy')}
                </time>
              </p>

              {project.user && (
                <>
                <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                  <circle r={1} cx={1} cy={1} />
                </svg>
                <p className="truncate">
                  {t("lead.views.create_by")} {project.createdBy} {" "}
                  {project.user.name}
                </p>
                </>
              )}
            </div>
          </div>
          <div className="w-96">
            <div className="flex items-start gap-x-3">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {t("task.views.labels")}
              </p>
            </div>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3  gap-2 text-xs leading-5 text-gray-500">
              {project?.labels && project?.labels?.map((label) => (
                <LabelUI
                  key={label.id}
                  labelKey={label.id}
                  background="bg-blue-50"
                  text="text-blue-700"
                  ring="ring-blue-600/20"
                  label={label.label}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-none items-center gap-x-4 mt-4 lg:mt-0">
            {!project.state ? (
              <ButtonPrimary
                text={t("lead.views.complete")}
                onClick={() => completeTask(project.id)}
                size="large"
              />
            ): null}
            <a
              onClick={() => onEdit(project)}
              href="#"
              className="text-primary-600 hover:text-primary-900 mr-4"
            >
              {t("general.edit")}
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
};
